import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Subject, Observable, of } from 'rxjs';
import { takeUntil, map, tap, catchError } from 'rxjs/operators';
import { ApiService } from '../../../services/api/api.service';
import { SearchQueryOptions } from '../../../services/api/search-query-options';
import { ComponentTypeExclusion, EventTypeExclusion, LockPolicyRuleValidation } from '../../../_models/lock-policy';
import { ComponentType } from '../../../_models/component-type';
import { EventType } from '../../../_models/event-type';
import { ConstantProperty } from '../../../_models/constant-property';
import {
    MatAccordion,
    MatExpansionPanel, MatExpansionPanelDescription,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle
} from "@angular/material/expansion";
import { NgTemplateOutlet, TitleCasePipe } from "@angular/common";
import {MatDivider} from "@angular/material/divider";
import {COMMON_FORM_IMPORTS} from "../../../shared/import-groups/Common";
import {MatIcon} from "@angular/material/icon";
import {
    SelectManyComponentTypesComponent
} from "../../../components/select-search-api/many/wrappers/select-many-component-types.component";
import {
    SelectManyEventTypesComponent
} from "../../../components/select-search-api/many/wrappers/select-many-event-types.component";
import {MatIconButton} from "@angular/material/button";
import {MatChip, MatChipListbox} from "@angular/material/chips";
import {MatProgressBar} from "@angular/material/progress-bar";

interface ExclusionGroup {
    type: 'component' | 'event';
    exclusions: Map<string, TypeExclusionState>;
    showAddPanel: boolean;
    selectedNewTypes: (ComponentType | EventType)[];
}

interface TypeExclusionState {
    id: string;
    name: string;
    isExpanded: boolean;
    isFullyExcluded: boolean;
    constantProperties: ConstantProperty[];
    selectedCPIds: Set<string>;
    cpLoading: boolean;
    cpLoaded: boolean;
}

@Component({
    selector: 'type-exclusions-panel',
    templateUrl: './type-exclusions-panel.component.html',
    styleUrls: ['./type-exclusions-panel.component.less'],
    imports: [
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatExpansionPanelDescription,
    NgTemplateOutlet,
    MatDivider,
    MatIcon,
    TitleCasePipe,
    SelectManyComponentTypesComponent,
    SelectManyEventTypesComponent,
    MatIconButton,
    MatChip,
    MatChipListbox,
    MatProgressBar,
    COMMON_FORM_IMPORTS
]
})
export class TypeExclusionsPanelComponent implements OnInit, OnDestroy {
    @Input() componentExclusions: ComponentTypeExclusion[] = [];
    @Input() eventExclusions: EventTypeExclusion[] = [];
    @Input() disabled: boolean = false;
    @Input() ruleValidation: LockPolicyRuleValidation | null = null;

    @Output() componentExclusionsChange = new EventEmitter<ComponentTypeExclusion[]>();
    @Output() eventExclusionsChange = new EventEmitter<EventTypeExclusion[]>();

    private destroy$ = new Subject<void>();

    componentGroup: ExclusionGroup = {
        type: 'component',
        exclusions: new Map(),
        showAddPanel: false,
        selectedNewTypes: []
    };

    eventGroup: ExclusionGroup = {
        type: 'event',
        exclusions: new Map(),
        showAddPanel: false,
        selectedNewTypes: []
    };


    get hasExclusions(): boolean {
        return this.componentGroup.exclusions.size > 0 || this.eventGroup.exclusions.size > 0;
    }

    constructor(
        private api: ApiService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.initializeFromInputs();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private initializeFromInputs(): void {
        // Initialize component exclusions
        if (this.componentExclusions.length > 0) {
            this.loadExclusionDetails('component', this.componentExclusions);
        }

        // Initialize event exclusions
        if (this.eventExclusions.length > 0) {
            this.loadExclusionDetails('event', this.eventExclusions);
        }
    }

    private loadExclusionDetails(type: 'component' | 'event', exclusions: (ComponentTypeExclusion | EventTypeExclusion)[]): void {
        const typeIds = exclusions.map(exc =>
            type === 'component'
                ? (exc as ComponentTypeExclusion).component_type_id
                : (exc as EventTypeExclusion).event_type_id
        );

        this.loadTypeDetails(type, typeIds).subscribe(types => {
            const group = type === 'component' ? this.componentGroup : this.eventGroup;

            types.forEach((typeData, index) => {
                const exclusion = exclusions[index];
                const cpIds = exclusion.constant_property_ids || [];

                const state: TypeExclusionState = {
                    id: typeData.id,
                    name: typeData.attributes.name,
                    isExpanded: false,
                    isFullyExcluded: false, // Not used for UI control anymore
                    constantProperties: [],
                    selectedCPIds: new Set(cpIds),
                    cpLoading: false,
                    cpLoaded: false
                };

                group.exclusions.set(typeData.id, state);
            });

            this.cdr.markForCheck();
        });
    }

    private loadTypeDetails(type: 'component' | 'event', typeIds: string[]): Observable<(ComponentType | EventType)[]> {
        if (typeIds.length === 0) {
            return of([]);
        }

        const options = new SearchQueryOptions();
        options.filters = [{ name: 'id', op: 'in', val: typeIds }];
        options.page_size = typeIds.length;

        const apiCall = type === 'component'
            ? this.api.component_type.searchMany(options)
            : this.api.event_type.searchMany(options);

        return apiCall.pipe(
            map(response => response.data || []),
            catchError(() => of([]))
        );
    }


    getGroupExclusionsArray(group: ExclusionGroup): [string, TypeExclusionState][] {
        return Array.from(group.exclusions.entries()).sort((a, b) =>
            a[1].name.localeCompare(b[1].name)
        );
    }

    toggleAddPanel(group: ExclusionGroup): void {
        group.showAddPanel = !group.showAddPanel;
        if (group.showAddPanel) {
            // Get current exclusions to show them as selected
            const currentExclusions = Array.from(group.exclusions.values());

            // Convert to the format expected by the select component
            group.selectedNewTypes = currentExclusions.map(exclusion => ({
                id: exclusion.id,
                attributes: { name: exclusion.name }
            })) as any[];
        } else {
            group.selectedNewTypes = [];
        }
    }

    onNewTypesSelected(group: ExclusionGroup, types: (ComponentType | EventType)[]): void {
        // Get the already excluded type IDs
        const existingIds = new Set(group.exclusions.keys());

        // Only add types that aren't already excluded
        types.forEach(type => {
            if (!existingIds.has(type.id)) {
                const state: TypeExclusionState = {
                    id: type.id,
                    name: type.attributes.name,
                    isExpanded: false,
                    isFullyExcluded: false, // Not used for UI control anymore
                    constantProperties: [],
                    selectedCPIds: new Set(),
                    cpLoading: false,
                    cpLoaded: false
                };
                group.exclusions.set(type.id, state);
            }
        });

        group.showAddPanel = false;
        group.selectedNewTypes = [];
        this.emitChanges();
        this.cdr.markForCheck();
    }

    toggleExpand(state: TypeExclusionState, group: ExclusionGroup): void {
        state.isExpanded = !state.isExpanded;

        if (state.isExpanded && !state.cpLoaded && !state.cpLoading) {
            this.loadConstantProperties(state, group.type);
        }
    }

    private loadConstantProperties(state: TypeExclusionState, type: 'component' | 'event'): void {
        state.cpLoading = true;

        const options = new SearchQueryOptions();

        // Use the correct relationship field names (plural) for the full API
        const relationFilter = type === 'component'
            ? { name: 'component_types', op: 'any', val: { name: 'id', op: 'eq', val: state.id } }
            : { name: 'event_types', op: 'any', val: { name: 'id', op: 'eq', val: state.id } };

        options.filters = [relationFilter];
        options.sort = 'name';
        options.page_size = 1000;

        // Use the full constant_property API instead of constant_property_light
        this.api.constant_property.searchMany(options).pipe(
            map(response => response.data || []),
            tap(cps => {
                state.constantProperties = cps;
                state.cpLoaded = true;
                state.cpLoading = false;

                // Don't auto-select any CPs - empty selection means all excluded

                this.cdr.markForCheck();
            }),
            catchError(error => {
                console.error('Error loading constant properties:', error);
                state.cpLoading = false;
                state.cpLoaded = true;
                state.constantProperties = [];
                this.cdr.markForCheck();
                return of([]);
            }),
            takeUntil(this.destroy$)
        ).subscribe();
    }


    toggleCP(state: TypeExclusionState, cpId: string): void {
        if (state.selectedCPIds.has(cpId)) {
            state.selectedCPIds.delete(cpId);
        } else {
            state.selectedCPIds.add(cpId);
        }

        this.emitChanges();
    }

    selectAllCPs(state: TypeExclusionState): void {
        if (state.selectedCPIds.size === 0) {
            // If all are excluded (none selected), select all to exclude none
            state.constantProperties.forEach(cp => {
                state.selectedCPIds.add(cp.id);
            });
        } else {
            // Clear all selections to exclude all
            state.selectedCPIds.clear();
        }
        this.emitChanges();
    }

    isAllCPsSelected(state: TypeExclusionState): boolean {
        // All CPs are excluded when none are selected
        return state.selectedCPIds.size === 0;
    }

    removeType(typeId: string, group: ExclusionGroup): void {
        group.exclusions.delete(typeId);
        this.emitChanges();
    }

    private emitChanges(): void {
        // Emit component exclusions
        const componentExclusions: ComponentTypeExclusion[] = [];
        this.componentGroup.exclusions.forEach((state, typeId) => {
            // If no CPs are selected, it means exclude all (empty array)
            // If some CPs are selected, exclude only those specific ones
            componentExclusions.push({
                component_type_id: typeId,
                constant_property_ids: state.selectedCPIds.size === 0 ? [] : Array.from(state.selectedCPIds)
            });
        });
        this.componentExclusionsChange.emit(componentExclusions);

        // Emit event exclusions
        const eventExclusions: EventTypeExclusion[] = [];
        this.eventGroup.exclusions.forEach((state, typeId) => {
            // If no CPs are selected, it means exclude all (empty array)
            // If some CPs are selected, exclude only those specific ones
            eventExclusions.push({
                event_type_id: typeId,
                constant_property_ids: state.selectedCPIds.size === 0 ? [] : Array.from(state.selectedCPIds)
            });
        });
        this.eventExclusionsChange.emit(eventExclusions);
    }

    trackByTypeId(index: number, item: [string, TypeExclusionState]): string {
        return item[0];
    }

    trackByCPId(index: number, cp: ConstantProperty): string {
        return cp.id;
    }

    getAvailableTypeIds(type: 'component' | 'event'): string[] {
        if (!this.ruleValidation?.affected) {
            return [];
        }
        return type === 'component'
            ? this.ruleValidation.affected.component_type_ids || []
            : this.ruleValidation.affected.event_type_ids || [];
    }

    getTypeFilters(type: 'component' | 'event'): any[] {
        const availableIds = this.getAvailableTypeIds(type);
        if (availableIds.length === 0) {
            return [];
        }

        // Show ALL available types from the rule validation
        // Don't filter out already selected ones - let the select component handle that
        return [{ name: 'id', op: 'in', val: availableIds }];
    }

    onNewTypesSelectionChange(group: ExclusionGroup, types: any[]): void {
        // Update the selected types with the full selection
        // The select component will handle maintaining the already excluded items
        group.selectedNewTypes = types || [];
        this.cdr.markForCheck();
    }

    hasAvailableTypes(type: 'component' | 'event'): boolean {
        const availableIds = this.getAvailableTypeIds(type);
        // Just check if there are any types available from the rule
        return availableIds.length > 0;
    }

    getAddButtonTooltip(type: 'component' | 'event'): string {
        if (this.disabled) {
            return '';
        }

        if (!this.ruleValidation) {
            return 'Configure a rule to enable type exclusions';
        }

        const availableIds = this.getAvailableTypeIds(type);
        if (availableIds.length === 0) {
            return `No ${type} types match the current rule`;
        }

        return '';
    }
}
